import { AxiosError, AxiosResponse } from 'axios'
import { AppDispatch, RootState } from '../../../store'
import { Customer } from './reducer'
import { errorLogger } from '../../../common/utils/errorLogger'
import { updateAppAlert } from '../../../common/modules/appAlert/actions'
import {
  patchCustomerDetails,
  postCustomerDetails,
} from '../../../common/api/Customers'
import { getErrorMessages } from '../../../common/utils/error'
import { RequestFailureMessage } from '../../../common/utils/messagesContants'
import { Dispatch } from 'redux'
import { addSeller } from '../../../cloudfaas/api/cloudfaas'
import {
  getPartnerIqFeatureKeysForSave,
  normalizeSubscribedProducts,
  wasPartnerIqEnabledInForm,
} from '../../../labraAi/utils/labraAiFeatures'
import {
  sendPartnerIqInvitations,
  updatePartnerIqOrgAccess,
} from '../../../labraAi/api/partnerIqAdmin'
import { triggerPartnerIqContactsCompute } from '../../../labraAi/api/partnerIqCompute'
import { actionTypeWrapper } from '../../../common/utils/actionTypeWrapper'
import { PartnerType, setAccount } from '../../../common/modules/partner/action'

export enum NewCustomerActions {
  UPDATE_NEW_CUSTOMER_FORM = 'UPDATE_NEW_CUSTOMER_FORM',
  RESET_NEW_CUSTOMER_FORM = 'RESET_NEW_CUSTOMER_FORM',
}

export const updateNewCustomer = (data: Customer) => ({
  type: NewCustomerActions.UPDATE_NEW_CUSTOMER_FORM,
  payload: data,
})

export const resetForm = () => ({
  type: NewCustomerActions.RESET_NEW_CUSTOMER_FORM,
})

const syncPartnerIqSideEffects = async (
  partnerId: string,
  customer: Customer,
  previousHadPartnerIq: boolean,
  fallbackEmails: string[] = []
) => {
  const nowHasPartnerIq = wasPartnerIqEnabledInForm(customer.subscribedProducts)
  const userEmails = [
    ...(customer.users ?? []).map(u => u.email?.trim()).filter(Boolean),
    ...fallbackEmails.map(e => e.trim()).filter(Boolean),
  ].filter((email, index, arr) => arr.indexOf(email) === index) as string[]

  if (nowHasPartnerIq && !previousHadPartnerIq) {
    await triggerPartnerIqContactsCompute(partnerId)
  }

  if (nowHasPartnerIq && !previousHadPartnerIq && userEmails.length > 0) {
    try {
      await sendPartnerIqInvitations({ partnerId, userEmails })
    } catch {
      // Backend endpoint may be pending — customer save still succeeds
    }
  }

  if (previousHadPartnerIq !== nowHasPartnerIq) {
    try {
      await updatePartnerIqOrgAccess(partnerId, nowHasPartnerIq)
    } catch {
      // Backend endpoint may be pending
    }
  }
}

export const submitCustomerForm =
  (customer: Customer) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    try {
      const payload = getPartnerIqFeatureKeysForSave(customer)
      const response = await postCustomerDetails(payload)
      if (customer.subscribedProducts.CLOUDFAAS) {
        const sellerData = {
          partner_id: response.data.partner_id,
          seller_email: customer?.users?.[0]?.email,
          seller_name: customer?.companyName,
        }
        await addSeller(sellerData)
      }
      if (
        response.data.partner_id &&
        wasPartnerIqEnabledInForm(payload.subscribedProducts)
      ) {
        await syncPartnerIqSideEffects(response.data.partner_id, payload, false)
      }
      await dispatch(
        updateAppAlert({
          message: 'New customer created successfully',
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
      return { success: true }
    } catch (error: unknown) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    }
  }

export const updateCustomerForm =
  (customer: Customer, partnerId: string) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const activeProducts =
      getState().AccountData.admin.partnerData?.activeProductsWithCloud || []
    const partnerEmail =
      getState().AccountData.admin.partnerData?.partnerOwner?.email || ''
    const previousSubscribed =
      getState().AccountData.admin.partnerData?.subscribedProducts ?? {}
    const previousHadPartnerIq = wasPartnerIqEnabledInForm(previousSubscribed)

    try {
      const payload = getPartnerIqFeatureKeysForSave(customer)
      const normalizedProducts = normalizeSubscribedProducts(
        payload.subscribedProducts
      )

      const updatedData = {
        companyName: customer.companyName,
        internalNotes: customer.internalNotes,
        subscribedProducts: normalizedProducts ?? payload.subscribedProducts,
        numberOfSeats: customer.numberOfSeats,
        isInternal: customer.isInternal,
        activeProducts: activeProducts,
        crmType: customer.crmType,
        partnershipType: customer.partnershipType,
        plan: customer.plan?.toUpperCase(),
      }
      await patchCustomerDetails(partnerId, updatedData)
      await syncPartnerIqSideEffects(
        partnerId,
        payload,
        previousHadPartnerIq,
        partnerEmail ? [partnerEmail] : []
      )
      const adminPartner = getState().AccountData.admin.partnerData
      if (adminPartner) {
        await dispatch(
          actionTypeWrapper(
            PartnerType.Admin,
            setAccount({
              ...adminPartner,
              subscribedProducts: normalizedProducts,
            })
          )
        )
      }
      dispatch(
        updateNewCustomer({
          ...payload,
          subscribedProducts: normalizedProducts ?? payload.subscribedProducts,
        })
      )
      if (customer.subscribedProducts.CLOUDFAAS) {
        const sellerData = {
          partner_id: partnerId,
          seller_email: partnerEmail,
          seller_name: customer?.companyName,
        }
        await addSeller(sellerData)
      }
      await dispatch(
        updateAppAlert({
          message: 'Customer updated successfully',
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: unknown) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    }
  }
